import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Lock, Eye, EyeOff, ArrowRight, AlertCircle } from "lucide-react";

function formatError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get("next") || "/";

  const [email, setEmail] = useState("alex@quowin.io");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isAuthenticated) navigate(nextPath, { replace: true });
  }, [isAuthenticated, navigate, nextPath]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate(nextPath, { replace: true });
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || err.message || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFBFF] text-[#0B1736] flex items-center justify-center p-6 relative overflow-hidden">
      {/* soft purple corner glow */}
      <div
        aria-hidden="true"
        className="absolute -top-32 -right-32 h-96 w-96 rounded-full opacity-40 pointer-events-none"
        style={{ background: "radial-gradient(closest-side, #DCE1F1, transparent)" }}
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full opacity-30 pointer-events-none"
        style={{ background: "radial-gradient(closest-side, #DCE1F1, transparent)" }}
      />

      <div className="relative w-full max-w-[420px] fade-in">
        {/* Brand */}
        <div className="flex flex-col items-center gap-2 mb-8">
          <img data-testid="brand-logo" src="/quowin-logo.png" alt="QuoWin" className="h-11 w-auto" />
          <div className="text-[10px] tracking-[0.18em] uppercase text-[#9098A8]">
            Quote to Win
          </div>
        </div>

        <div className="quo-card p-7">
          <h1 className="font-display text-[26px] font-medium tracking-tight text-[#0B1736]">
            Sign in
          </h1>
          <p className="text-[13.5px] text-[#5F6677] mt-1">
            Welcome back. Use your workspace credentials.
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-[11px] font-semibold uppercase tracking-wide text-[#9098A8]">
                Email
              </Label>
              <div className="relative mt-1.5">
                <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9098A8]" />
                <Input
                  id="email"
                  data-testid="login-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="alex@quowin.io"
                  className="pl-9 h-11 text-[14px] bg-white border-[#E8EAF3] focus-visible:ring-[#011B90]/30 focus-visible:border-[#011B90]"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-[11px] font-semibold uppercase tracking-wide text-[#9098A8]">
                Password
              </Label>
              <div className="relative mt-1.5">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9098A8]" />
                <Input
                  id="password"
                  data-testid="login-password"
                  type={show ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pl-9 pr-10 h-11 text-[14px] bg-white border-[#E8EAF3] focus-visible:ring-[#011B90]/30 focus-visible:border-[#011B90]"
                  required
                />
                <button
                  type="button"
                  data-testid="login-toggle-visibility"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 h-7 w-7 grid place-items-center rounded-md text-[#9098A8] hover:text-[#0B1736] hover:bg-[#EDEFF7]"
                  aria-label={show ? "Hide password" : "Show password"}
                >
                  {show ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="flex items-start gap-2 text-[12.5px] text-[#B4232C] bg-[#FDECEE] border border-[#F5C7CC] rounded-md px-3 py-2"
              >
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={submitting}
              className="w-full h-11 bg-[#011B90] hover:bg-[#011670] text-white rounded-md shadow-[0_8px_24px_-8px_rgba(1, 27, 144,0.55)] disabled:opacity-70 flex items-center justify-center gap-2"
            >
              {submitting ? "Signing in…" : (
                <>
                  Sign in <ArrowRight size={16} />
                </>
              )}
            </Button>
          </form>

          {/* Demo credentials */}
          <div
            data-testid="demo-credentials"
            className="mt-6 rounded-lg border border-dashed border-[#E8EAF3] bg-[#FAFBFF] p-3.5"
          >
            <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#9098A8]">
              Demo credentials
            </div>
            <div className="mt-1 font-mono text-[12.5px] text-[#0B1736]">
              alex@quowin.io &nbsp;·&nbsp; quowin123
            </div>
            <div className="mt-1 text-[11.5px] text-[#9098A8]">
              This is a single-workspace MVP — no signup needed.
            </div>
          </div>
        </div>

        <p className="text-center text-[11.5px] text-[#9098A8] mt-6">
          Protected workspace · QuoWin © {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
